import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	FileText,
	Search,
	Filter,
	Calendar,
	ChevronLeft,
	ChevronRight,
	User,
	Building2,
	Settings,
	Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import { getAccessToken } from "@/services/auth";
import { adminApiUrl } from "@/config";

// Fetch functions
async function fetchAuditLogs(params: Record<string, any>) {
	const token = getAccessToken();
	const queryParams = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value) queryParams.append(key, String(value));
	});
	const res = await fetch(adminApiUrl(`/audit-log?${queryParams.toString()}`), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchAuditStats(period: string) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl(`/audit-log/stats?period=${period}`), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

const targetTypeIcons: Record<string, React.ElementType> = {
	organization: Building2,
	user: User,
	system_admin: Shield,
	plan: Settings,
	subscription: Settings,
	custom_package: Settings,
	feature: Settings,
	system: Settings,
};

const actionColors: Record<string, string> = {
	create: "bg-green-100 text-green-700",
	update: "bg-blue-100 text-blue-700",
	delete: "bg-red-100 text-red-700",
	login: "bg-purple-100 text-purple-700",
	logout: "bg-gray-100 text-gray-700",
};

export function AuditLog() {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [targetType, setTargetType] = useState("");
	const [showFilters, setShowFilters] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["audit-logs", page, search, targetType],
		queryFn: () =>
			fetchAuditLogs({
				page,
				limit: 25,
				search: search || undefined,
				targetType: targetType || undefined,
			}),
	});

	const { data: stats } = useQuery({
		queryKey: ["audit-stats"],
		queryFn: () => fetchAuditStats("7d"),
	});

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setSearch(searchInput);
		setPage(1);
	};

	const getActionColor = (action: string) => {
		const type = action.split(".")[1] || action.split(".")[0];
		return actionColors[type] || "bg-gray-100 text-gray-700";
	};

	const targetTypes = [
		"organization",
		"user",
		"system_admin",
		"plan",
		"subscription",
		"custom_package",
		"feature",
		"system",
	];

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
					<p className="text-gray-500 mt-1">
						Track all admin actions and system changes
					</p>
				</div>
			</div>

			{/* Stats */}
			{stats && (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-blue-100 rounded-lg">
									<FileText className="w-5 h-5 text-blue-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">{stats.totalLogs}</p>
									<p className="text-sm text-gray-500">Total (7 days)</p>
								</div>
							</div>
						</CardContent>
					</Card>
					{stats.byAction?.slice(0, 3).map((item: any) => (
						<Card key={item.action}>
							<CardContent className="p-4">
								<p className="text-2xl font-bold">{item.count}</p>
								<p className="text-sm text-gray-500 truncate">{item.action}</p>
							</CardContent>
						</Card>
					))}
				</div>
			)}

			{/* Search and Filters */}
			<Card>
				<CardContent className="p-4">
					<div className="flex flex-col md:flex-row gap-4">
						<form onSubmit={handleSearch} className="flex-1 flex gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<Input
									placeholder="Search by admin, action, or target..."
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									className="pl-10"
								/>
							</div>
							<Button type="submit" variant="outline">
								Search
							</Button>
						</form>
						<Button
							variant="outline"
							onClick={() => setShowFilters(!showFilters)}
							className={cn(showFilters && "bg-gray-100")}
						>
							<Filter className="w-4 h-4 mr-2" />
							Filters
						</Button>
					</div>

					{/* Filter Panel */}
					{showFilters && (
						<div className="mt-4 pt-4 border-t flex flex-wrap gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Target Type
								</label>
								<select
									value={targetType}
									onChange={(e) => {
										setTargetType(e.target.value);
										setPage(1);
									}}
									className="px-3 py-2 border rounded-md text-sm"
								>
									<option value="">All Types</option>
									{targetTypes.map((type) => (
										<option key={type} value={type}>
											{type.replace(/_/g, " ")}
										</option>
									))}
								</select>
							</div>
							{(search || targetType) && (
								<div className="flex items-end">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => {
											setSearch("");
											setSearchInput("");
											setTargetType("");
											setPage(1);
										}}
									>
										Clear Filters
									</Button>
								</div>
							)}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Audit Logs Table */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span>Audit Logs</span>
						{data && (
							<span className="text-sm font-normal text-gray-500">
								{data.pagination.total} total
							</span>
						)}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
						</div>
					) : data?.logs?.length === 0 ? (
						<div className="text-center py-12 text-gray-500">
							No audit logs found
						</div>
					) : (
						<>
							<div className="space-y-3">
								{data?.logs?.map((log: any) => {
									const IconComponent =
										targetTypeIcons[log.targetType] || FileText;
									return (
										<div
											key={log._id}
											className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50"
										>
											<div className="p-2 bg-gray-100 rounded-lg">
												<IconComponent className="w-5 h-5 text-gray-500" />
											</div>
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 flex-wrap">
													<span className="font-medium">{log.adminName}</span>
													<span className="text-gray-400">
														({log.adminEmail})
													</span>
													<Badge className={getActionColor(log.action)}>
														{log.action}
													</Badge>
												</div>
												<div className="mt-1 text-sm text-gray-600">
													<span className="capitalize">
														{log.targetType.replace(/_/g, " ")}
													</span>
													{log.targetName && (
														<>
															:{" "}
															<span className="font-medium">{log.targetName}</span>
														</>
													)}
												</div>
												{(log.previousValue || log.newValue) && (
													<div className="mt-2 text-xs text-gray-500 space-y-1">
														{log.previousValue && (
															<div>
																<span className="text-red-600">- </span>
																{JSON.stringify(log.previousValue).slice(0, 100)}
																...
															</div>
														)}
														{log.newValue && (
															<div>
																<span className="text-green-600">+ </span>
																{JSON.stringify(log.newValue).slice(0, 100)}...
															</div>
														)}
													</div>
												)}
											</div>
											<div className="text-right text-sm text-gray-500 whitespace-nowrap">
												<div className="flex items-center gap-1">
													<Calendar className="w-3 h-3" />
													{new Date(log.createdAt).toLocaleDateString()}
												</div>
												<div>
													{new Date(log.createdAt).toLocaleTimeString()}
												</div>
											</div>
										</div>
									);
								})}
							</div>

							{/* Pagination */}
							{data && data.pagination.pages > 1 && (
								<div className="flex items-center justify-between mt-4 pt-4 border-t">
									<p className="text-sm text-gray-500">
										Page {data.pagination.page} of {data.pagination.pages}
									</p>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											disabled={page <= 1}
											onClick={() => setPage((p) => p - 1)}
										>
											<ChevronLeft className="w-4 h-4 mr-1" />
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											disabled={page >= data.pagination.pages}
											onClick={() => setPage((p) => p + 1)}
										>
											Next
											<ChevronRight className="w-4 h-4 ml-1" />
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
